import React, { useMemo, useState } from 'react';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { WithSkeleton } from '@r1-frontend/ui-react/components/loaders/Skeleton';
import DefaultPopup from '@r1-frontend/ui-react/components/popups/defaultPopup';
import { ListContainer } from '@r1-frontend/ui-react/experimental/containers';
import { TvSubscription } from '@r1-frontend/ui-react/organisms/TariffSubscription';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';

import addEntity from '~/src/features/tariffBuilder/actions/addEntity';
import removeEntity from '~/src/features/tariffBuilder/actions/removeEntity';
import { useConstructorAnalytics } from '~/src/features/tariffBuilder/analytics/hooks/useConstructorAnalytics';
import OnlineCinemaAdditionalInfo from '~/src/features/tariffBuilder/components/popups/OnlineCinemaAdditionalInfo';
import { useCtxDispatch, useCtxSelector } from '~/src/features/tariffBuilder/hooks';
import getEntities from '~/src/features/tariffBuilder/selectors/getEntities';
import { EEntity } from '~/src/features/tariffBuilder/types/EEntity';

type TOwnState = {
    selectedId: undefined,
    isPopupOpen: false,
} | {
    selectedId: number,
    isPopupOpen: true,
}

type TProps = {
    entityName: EEntity.onlineCinemas | EEntity.antivirus | EEntity.security | EEntity.additional,
}

const EntitiesContainer = ({ entityName }: TProps): JSX.Element => {
    const isMobile = useMatchMediaByWidth(wideBreakpoints.mobile);
    const entities  = useCtxSelector(state => getEntities(entityName)(state));
    const dispatch = useCtxDispatch();
    const [{ selectedId, isPopupOpen }, setState] = useState<TOwnState>({
        selectedId: undefined,
        isPopupOpen: false,
    });

    const analytics = useConstructorAnalytics();

    const entitiesMap = useMemo(() => {
        return new Map(entities.map(e => [e.key, e]));
    }, [entities]);

    const handleSelect = (key: number) => () => {
        const entity = entitiesMap.get(key);
        if (entity && !entity.isSelected) {
            dispatch(addEntity({
                entity: entityName,
                title: entity.title,
                key,
            },
            analytics));
        } else {
            dispatch(removeEntity({
                entity: entityName,
                key,
            }));
        }
    };

    const handleIconClick = (key: number) => () => {
        setState({ isPopupOpen: true, selectedId: key });
    };

    const handleClosePopup = () => {
        setState({ selectedId: undefined, isPopupOpen: false });
    };

    return <>
        <ListContainer $mobile={8} $desktop={16} $direction={isMobile ? 'column' : 'row'}>
            <WithSkeleton
                height={304}
                width={'33%'}
                count={3}
                inline
                containerClassName={'react-skeleton-flex-1 fd-row gap16'}
            ><>
                {entities.map(({ title, isSelected, cost, key, ...item }) => (
                    <TvSubscription
                        key={`SubscriptionEntity_${item.id}`}
                        onToggle={handleSelect(key)}
                        onIconClick={handleIconClick(key)}
                        checked={isSelected}
                        cost={cost.fact}
                        oldCost={cost.previous}
                        {...item}
                    >
                        {title}
                    </TvSubscription>
                ))}
            </></WithSkeleton>
        </ListContainer>

        {(isPopupOpen && selectedId) && (
            <DefaultPopup
                isOpen={true}
                fullWidth={false}
                onClose={handleClosePopup}
            >
                <OnlineCinemaAdditionalInfo
                    titlePrefix={entityName === EEntity.onlineCinemas ? 'Онлайн кинотеатр' : null}
                    onClose={handleClosePopup}
                    subscription={entitiesMap.get(selectedId)}
                />
            </DefaultPopup>
        )}
    </>;
};

export default EntitiesContainer;
